import { environment } from "../../environments/environment";
import { KeyValueInterface } from "../_model/form/interface/key-value.interface";
import { LookupModel } from "../_model/metadata/lookup.model";
import { PropertyModel } from "../_model/property/property.model";
import { camelToPascal, exists, isArray, normalize } from "./util.helper";
import damerauLevenshtein from 'damerau-levenshtein';
import jaroWinkler from 'jaro-winkler';
import { ShapeResult } from "../_service/shape.service";

/**
 * Converts an mapping (path to where to store the data) to a RESO standard name
 */
export function mappingToStandardName(mapping: string): string {
  return mapping?.substring(mapping.lastIndexOf('.') + 1, mapping.length) || '';
}

/**
 * Filters the lookups list to just the lookups for a particular mapping.
 */
export function getAllLookups(lookups: LookupModel[], mapping: string): LookupModel[] {
  let _mapping = mappingToStandardName(mapping);
  return lookups.filter(lookup => lookup.lookupName === _mapping);
}

/**
 * Filters the lookups list to just the lookups for a particular mapping.
 * Excludes draft lookups.
 */
export function getLookups(lookups: LookupModel[], mapping: string): LookupModel[] {
  let _mapping = mappingToStandardName(mapping);
  return lookups.filter(lookup => lookup.lookupName === _mapping && lookup.lookupStatus !== 'Draft');
}

/**
 * Gets a key/value pair array of lookups for a particular mapping.
 * Excludes draft lookups.
 */
export function getLookupsKeysValues(lookups: LookupModel[], mapping: string): KeyValueInterface<LookupModel>[] {
  return getLookups(lookups, mapping).map(lookup => { return {
    key: lookup.lookupValue,
    value: lookup.lookupDisplayName,
    other: lookup
  }});
}

/**
 * Gets a key/value pair array of lookups for a particular mapping, filtered by lookup constraint.
 * Excludes draft lookups.
 */
export function getLookupsKeysValuesWithConstraints(json: any, lookups: LookupModel[], mapping: string): KeyValueInterface<LookupModel>[] {
  return getLookups(lookups, mapping).filter(l => {
    if (!json || !l.constraints?.length) return true;
    for (let constraints of l.constraints) {
      let passed = true;
      for (let constraint of constraints) {
        let value = json[constraint.standardName];
        if (
          !value ||
          constraint.allowed?.length && !constraint.allowed.includes(value) ||
          constraint.disallowed?.length && constraint.disallowed.includes(value)
        ) {
          passed = false;
        }
        if (!passed) break;
      }
      if (passed) return true;
    }
    return false;
  }).map(l => { return {
    key: l.lookupValue,
    value: l.lookupDisplayName,
    other: l
  }});
}

/**
 * A poor attempt to normalize this shit data we get to RESO standards
 */
export function normalizeListing(listing: PropertyModel, lookups: LookupModel[]) {
  if (listing.StreetName && !listing.StreetSuffix) {
    //if we don't have a StreetSuffix, good chance they stuffed it into the StreetName
    let potentialStreetSuffix = listing.StreetName.split(' ').pop();
    let ssKVs = getLookupsKeysValues(lookups, 'StreetSuffix').filter(kv => !kv.other?.lookupStatus || kv.other.lookupStatus === 'Active' || kv.other.lookupStatus === 'Permitted');
    let realStreetSuffix = match('StreetSuffix', ssKVs, potentialStreetSuffix);
    if (realStreetSuffix) {
      listing.StreetName = listing.StreetName.substring(0, listing.StreetName.lastIndexOf(' '));
      listing.StreetSuffix = realStreetSuffix;
    }
  }
  //set properties
  let old = {};
  for (let key in listing) {
    if (exists(listing[key])) {
      let kvs = getLookupsKeysValues(lookups, key).filter(kv => !kv.other?.lookupStatus || kv.other.lookupStatus === 'Active' || kv.other.lookupStatus === 'Permitted');
      if (kvs.length) {
        old[key] = listing[key];
        if (typeof listing[key] === 'string' && !listing[key].includes(',')) {
          listing[key] = match(key, kvs, listing[key]);
        } else if (isArray(listing[key])) {
          listing[key] = listing[key].map(v => match(key, kvs, v)).filter(v => !!v);
        } else if (typeof listing[key] === 'string' && listing[key].includes(',')) {
          listing[key] = listing[key].split(',').map(v => match(key, kvs, v)).filter(v => !!v).join(',');
        }
        if (!exists(listing[key]) || (
          isArray(listing[key]) && !listing[key].length
        )) delete listing[key];
      }
    }
  }
}

function match(key: string, kvs: KeyValueInterface<LookupModel>[], value: string): string | null {
  let exactMatch = kvs.find(kv => kv.value.toLowerCase() === value.toLowerCase() || kv.key.toLowerCase() === value.toLowerCase());
  if (exactMatch) {
    environment.log(`[exact match] ${key}: ${value} > ${exactMatch.key} (${exactMatch.value})`);
    return exactMatch.key;
  }
  let normalValue = normalize(value);
  let closeMatch = kvs.find(kv => normalize(kv.value) === normalValue || normalize(kv.key) === normalValue);
  if (closeMatch) {
    environment.log(`[close match] ${key}: ${value} > ${closeMatch.key} (${closeMatch.value})`);
    return closeMatch.key;
  }

  let scored = kvs
    //normalize the value to compare
    .map(kv => ({ key: kv.key, value: kv.value, normalValue: normalize(kv.value) }))
    //score length
    .map(kv => ({ ...kv, length: lengthScore(kv.normalValue, normalValue) }))
    .filter(kv => kv.length > 0.1)
    //score character
    .map(kv => ({ ...kv, character: characterScore(kv.normalValue, normalValue) }))
    .filter(kv => kv.character > 0.1)
    //score sequence
    .map(kv => ({ ...kv, sequence: sequenceScore(kv.normalValue, normalValue) }))
    .filter(kv => kv.sequence > 0.1)
    //score reverse
    .map(kv => ({ ...kv, reverse: kv.sequence === 1 ? 1 : reverseScore(kv.normalValue, normalValue) }))
    .filter(kv => kv.reverse > 0.1)
    //score start
    .map(kv => ({ ...kv, start: startScore(kv.normalValue, normalValue) }))
    // .filter(kv => kv.start > 0.1)
    //score end
    .map(kv => ({ ...kv, end: kv.start === 1 ? 1 : endScore(kv.normalValue, normalValue) }))
    // .filter(kv => kv.end > 0.1)
    //score without vowels (helps w/ abbreviations)
    .map(kv => ({ ...kv, vowelless: vowelless(kv.normalValue, normalValue) }))
    .filter(kv => kv.vowelless > 0.1)
    //score start/end characters (helps w/ abbreviations)
    .map(kv => ({ ...kv, startEnd: startEnd(kv.normalValue, normalValue) }))
    .filter(kv => kv.startEnd > 0.1)
    //score similarity
    .map(kv => ({ ...kv, jaroWinkler: jaroWinkler(kv.normalValue, normalValue) }))
    .filter(kv => kv.jaroWinkler > 0.1)
    //score similarity + steps
    .map(kv => ({ ...kv, ...damerauLevenshtein(kv.normalValue, normalValue) }))
    .filter(kv => kv.similarity > 0.1)
    .filter(kv => kv.steps <= Math.abs(kv.normalValue.length - normalValue.length))
    //weight scores
    .map(kv => (delete kv.normalValue) && ({
      ...kv,
      // these don't need to add up to 1, but it's satisfying if they do
      score: (
        kv.length      * 0.02 +
        kv.character   * 0.24 +
        kv.sequence    * 0.36 +
        kv.reverse     * 0.04 +
        kv.start       * 0.04 +
        kv.end         * 0.02 +
        kv.vowelless   * 0.02 +
        kv.startEnd    * 0.02 +
        kv.jaroWinkler * 0.04 +
        kv.similarity  * 0.20
      )
    }))
    .filter(kv => kv.score > 0)
    .sort((a, b) => a.score === b.score ? 0 : b.score > a.score ? 1 : -1);

  if (scored.length && scored[0].score >= 0.8) {
    environment.log(`[${scored[0].score.toFixed(3)} match] ${key}: ${value} > ${scored[0].key} (${scored[0].value})`, scored.slice(0, 3));
    return scored[0].key;
  }

  environment.log(`[  no  match] ${key}: ${value} > null`, scored.slice(0, 3));
  return null;
}

function lengthScore(str1: string, str2: string): number {
  let shortest = str1.length <= str2.length ? str1 : str2;
  let longest = str1.length > str2.length ? str1 : str2;
  return shortest.length > 0 && longest.length > 0 ? shortest.length / longest.length : 0;
}

function characterScore(str1: string, str2: string): number {
  let shortest = str1.length <= str2.length ? str1 : str2;
  let longest = str1.length > str2.length ? str1 : str2;
  let score = 0;
  for (let char of shortest) {
    if (longest.includes(char)) score++;
  }
  return score / shortest.length;
}

function sequenceScore(str1: string, str2: string): number {
  let shortest = str1.length <= str2.length ? str1 : str2;
  let longest = str1.length > str2.length ? str1 : str2;
  let score = 0;
  for (let char of shortest) {
    let index = longest.indexOf(char);
    if (index === -1) return score / shortest.length;
    else {
      score++;
      longest = longest.substring(index+1);
    }
  }
  return score / shortest.length;
}

function reverseScore(str1: string, str2: string): number {
  return sequenceScore(
    reverseString(str1),
    reverseString(str2)
  );
}

function startScore(str1: string, str2: string) {
  let shortest = str1.length <= str2.length ? str1 : str2;
  let longest = str1.length > str2.length ? str1 : str2;
  let score = 0;
  for (let char of shortest) {
    let index = longest.indexOf(char);
    if (index !== 0) return score / shortest.length;
    else {
      score++;
      longest = longest.substring(index+1);
    }
  }
  return score / shortest.length;
}

function endScore(str1: string, str2: string): number {
  return startScore(
    reverseString(str1),
    reverseString(str2)
  );
}

function vowelless(str1: string, str2: string) {
  let string1 = str1.replace(/[AEIOU]/g, '');
  let string2 = str2.replace(/[AEIOU]/g, '');
  let shortest = string1.length <= string2.length ? string1 : string2;
  let longest = string1.length > string2.length ? string1 : string2;
  let length = shortest.length > 0 && longest.length > 0 ? shortest.length / longest.length : 0;
  let score = 0;
  for (let char of shortest) {
    let index = longest.indexOf(char);
    if (index !== 0) return score / shortest.length;
    else {
      score++;
      longest = longest.substring(index+1);
    }
  }
  let sequence = score / shortest.length;
  return (length + sequence) * 0.5;
}

function startEnd(str1: string, str2: string) {
  let score = 0;
  if (str1.startsWith(str2[0])) score += 0.5;
  if (str1.endsWith(str2[str2.length-1])) score += 0.5;
  return score;
}

function reverseString(string: string) {
  let reversed = '';
  for(let i = string.length-1; i >= 0; i--) {
    reversed += string[i];
  }
  return reversed;
}

export function normalizeShape(shape: ShapeResult, lookups: LookupModel[]): ShapeResult {
  for (let key of [ 'countyOrParish', 'cityRegion', 'city', 'stateOrProvince' ]) {
    let pascalKey = camelToPascal(key);
    let kvs = getLookupsKeysValues(lookups, pascalKey);
    let found = match(pascalKey, kvs, shape[key]);
    if (found) shape[key] = found;
  }
  return shape;
}